import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
  Renderer2,
  computed,
  signal,
  inject,
  input
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IEndpoint, StreamTypes } from "@auvious/rtc";
import { merge } from "rxjs";
import { filter } from "rxjs/operators";
import {
  AgentParam,
  IInteraction,
  CustomerParam,
  SpeechToTextMetadata,
  StreamStateEnum,
  ThemeParam
} from "../../models";
import {
  EndpointTypeEnum,
  SNAPSHOT_TYPE_VISION,
  StreamTrackKindEnum,
  UserRoleEnum
} from "../../core-ui.enums";
import { TranscriptService } from "../../services/transcript.service";
import { TileComponent } from "../tile/tile.component";
import { RoomComponent } from "../room/room.component";
import {
  AppConfigService,
  AssistantService,
  AuviousRtcService,
  ConferenceService,
  ConferenceStore,
  EndpointState,
  isDebugRtcOn,
  PointerService,
  SnapshotService,
  StreamState,
  UserService,
  ThemeService,
  ArPointerActivatedEvent
} from "../../services";
@Component({
  selector: "app-tile-info",
  templateUrl: "./tile-info.component.html",
  styleUrls: ["./tile-info.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TileInfoComponent implements OnInit {
  private rtc = inject(AuviousRtcService);
  private config = inject(AppConfigService);
  private conference = inject(ConferenceService);
  private snapshot = inject(SnapshotService);
  private cdr = inject(ChangeDetectorRef);
  private pointer = inject(PointerService);
  private asr = inject(TranscriptService);
  private pointerService = inject(PointerService);
  private store = inject(ConferenceStore);
  private assistant = inject(AssistantService);
  private conferenceStore = inject(ConferenceStore);
  private themeService = inject(ThemeService);
  private tile = inject(TileComponent, { optional: true });
  private room = inject(RoomComponent, { optional: true });
  private destroyRef = inject(DestroyRef);

  @Input()
  set stream(value: StreamState) {
    this._stream = value;

    this.isMutedMicAria.set(
      this.conference.isMutedStreamId(
        this._stream.id,
        StreamTrackKindEnum.audio
      )
    );

    this.isMutedMic.set(
      this.conference.isMutedStreamId(
        this._stream.id,
        StreamTrackKindEnum.audio
      ) &&
        !this.conference.isMutedStreamId(
          this._stream.id,
          StreamTrackKindEnum.video
        )
    );

    this.isMutedCam.set(
      this.conference.isMutedStreamId(
        this._stream.id,
        StreamTrackKindEnum.video
      )
    );
  }
  get stream() {
    return this._stream;
  }
  readonly interaction = input<IInteraction>(undefined);

  isAgent = false;
  isCustomer = false;
  // snapshots: ISnapshot[] = [];
  snapshotCount = 0;
  visionCount = 0;
  isCaptionsOn = signal(false);
  isTileTooSmall = signal(false);
  _stream: StreamState;
  _endpointState = signal<EndpointState>(undefined);
  _streamState = signal<StreamState>(undefined);

  isPointerActive = signal(false);
  isPinned = computed(() => this.room?.pinnedTile()?.uuid === this.tile?.uuid);
  isMutedMic = signal(false);
  isMutedMicAria = signal(false);
  isMutedCam = signal(false);
  color = signal(undefined);
  colors = signal<string[]>([]);
  assignedColors = signal<string[]>([]);
  isColorPickerOpen = signal(false);

  constructor() {
    const userService = inject(UserService);

    this.isAgent = userService.isAgent;
    this.isCustomer = userService.isCustomer;
  }

  ngOnInit(): void {
    this.colors.set(
      this.themeService.getOptions()[ThemeParam.AR_POINTER_COLORS]
    );

    this.snapshot.snapshotsAvailable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((snapshots) => {
        this.snapshotCount = snapshots.filter(
          (s) =>
            s.type !== SNAPSHOT_TYPE_VISION &&
            s.userId === this.stream.originator.username
        ).length;
        this.visionCount = snapshots.filter(
          (s) =>
            s.type === SNAPSHOT_TYPE_VISION &&
            s.userId === this.stream.originator.username
        ).length;
        this.cdr.detectChanges();
      });
    this.pointer.pointColorsChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((colors) => {
        const color = colors[this.stream.originator.endpoint];
        this.assignedColors.set(Object.values(colors));
        this.color.set(color);
        if (Object.values(colors).length === 0) {
          this.isColorPickerOpen.set(false);
        }
      });
    this.pointer.pointAreaChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.isPointerActive.set(event.type === ArPointerActivatedEvent.type);
      });
    merge(
      this.conference.conferenceMetadataSet$.pipe(
        filter((m) => m instanceof SpeechToTextMetadata)
      ),
      this.asr.enabled$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this.isCaptionsOn.set(true);
      });
    merge(
      this.conference.conferenceMetadataRemoved$.pipe(
        filter((m) => m instanceof SpeechToTextMetadata)
      ),
      this.asr.disabled$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this.isCaptionsOn.set(false);
      });
    this.conferenceStore.updated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        this._endpointState.set(
          this.conferenceStore.getEndpoint(this.stream.originator.endpoint)
        );
        this._streamState.set(this.conferenceStore.getStream(this.stream.id));
      });

    if (this.tile) {
      this.tile.viewSizeChanged
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((size) => {
          this.isTileTooSmall.set(size.width < 180 || size.height < 180);
        });
    }
  }

  snapshotRequest() {
    if (
      !this.snapshot.isStarted &&
      !this.pointer.isStarted &&
      !this.store.screenStream &&
      !this.isWhisper
    ) {
      this.snapshot.request(this.stream.id);
    }
  }

  toggleAssistant() {
    if (!this.isWhisper) {
      this.assistant.toggleOpen(true);
    }
  }

  changeColor(c: string) {
    if (this.isColorDisabled(c)) return;
    this.pointerService.claimColor(c, this.myself);
    this.isColorPickerOpen.set(false);
  }

  isColorDisabled(color: string) {
    return this.assignedColors().includes(color);
  }

  unpin() {
    this.room?.unpinTile();
  }

  isHidden = computed(
    () =>
      this.isPointerActive() &&
      !this.pointerService.isTarget(
        this.stream.originator.endpoint,
        this.stream.type,
        this.stream.correlationId
      ) &&
      this.isTileTooSmall()
  );

  get isWhisper() {
    return !!this.interaction().getWhisperMode();
  }

  get participantLanguage(): string {
    return this.participantMetadata?.language;
  }

  get isScreen() {
    return this.stream?.type === StreamTypes.SCREEN;
  }

  get isRoleAgent() {
    return (
      this.participantMetadata?.roles?.includes(UserRoleEnum.agent) ||
      this.isAIAssistant
    );
  }

  get participantMetadata() {
    const participants = this.conference.getParticipants();
    const participant = participants?.[this.stream.originator.endpoint];
    return participant?.metadata || {};
  }

  get isDebugRtc() {
    return isDebugRtcOn();
  }

  get myself(): IEndpoint {
    return this.rtc.myself;
  }

  get isLocal() {
    return this.stream.originator.endpoint === this.myself.endpoint;
  }

  get isNetworkIndicatorVisible() {
    return this.isAgent && !this.isLocal;
  }

  get isRoleAgentVisible() {
    return !this.isLocal && this.isRoleAgent;
  }

  get isAIAssistant() {
    return this.participantMetadata?.type === EndpointTypeEnum.aiAssistant;
  }

  get isParticipantLanguageEnabled() {
    return (
      this.isAgent &&
      !this.isLocal &&
      this.config.agentParamEnabled(AgentParam.CUSTOMER_LANGUAGE)
    );
  }

  // get snapshotCount() {
  //   const count = this.snapshots.filter(
  //     (s) => s.userId === this.stream.originator.username
  //   ).length;
  //   return count;
  // }

  get isParticipantControlsAvailable() {
    return (
      (this.isCustomer &&
        this.config.customerParamEnabled(
          CustomerParam.PARTICIPANT_CONTROLS_ENABLED
        )) ||
      !this.isCustomer
    );
  }

  isGroupOneAvailable = computed(
    () =>
      this.isDebugRtc ||
      (!this.isScreen &&
        ((this.isRoleAgentVisible && !this.isHidden()) ||
          (this.isNetworkIndicatorVisible && !this.isHidden()) ||
          this.isMutedMic() ||
          (this.isCaptionsOn() && !this.isHidden()) ||
          (this.color() && this.isPointerActive()) ||
          (this.isParticipantLanguageEnabled && !this.isHidden()) ||
          this.isViewerDisabled))
  );

  isWarning = computed(
    () =>
      this.isViewerDisabled ||
      this.isEndpointSick() ||
      this.isStreamSick() ||
      this.isStreamFailed()
  );

  public get isViewerDisabled() {
    return this.stream.video.disabledFromBitrateAdaptation;
  }

  isStreamSick = computed(
    () => this._streamState()?.state === StreamStateEnum.Warning
  );

  isStreamFailed = computed(
    () => this._streamState()?.state === StreamStateEnum.Failed
  );

  isEndpointSick = computed(
    () => this._endpointState()?.connectivity === "unstable"
  );
}
